import React, {
  useLayoutEffect,
  useEffect,
  useState,
  useContext,
  useRef,
} from 'react'
import { useNavigate } from 'react-router-dom'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import PackageDetailsContext from '../../context/PackageDetails/PackageDetails'

import AuthService from '../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import {
  checkPermission,
  removeFullHeightContainer,
  fullHeightContainer,
} from '../../utilities/helpers'

import { useFromPage } from '../../providers/FromPageProvider'
import { useModal } from '../../providers/ModalProvider/ModalProvider'
import { useSheet } from '../../providers/SideSheetProvider/SideSheetProvider'

import {
  getPackageMap,
  getPackageSeatIdentities,
  updateSeatsInPackage,
} from '../../utilities/api'

import useSafeAsync from '../../utilities/useSafeAsync'

import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { Confirm } from '../ModalContent'
import { SavingOverlay } from '../SavingOverlay'
import { ConfigureMap } from './ConfigureMap'
import { CurrentSelected } from '../../providers/SideSheetProvider/SheetContent/CurrentSelected'

const normalizeSeatId = (seatId) => String(seatId ?? '').trim()

export default function PackageSeatmapWrapper({ uuid }) {
  const safeSetState = useSafeAsync()
  const navigate = useNavigate()

  const { openSheet, closeSheet } = useSheet()
  const { openModal, closeModal } = useModal()
  const { isAssigningToFan } = useFromPage()

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const { orgPermissions } = useContext(UserContext)

  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.SEASON_PACKAGES_MANAGEMENT
    )

  const { updateCanPublish, name, isPackagePublished, hasPackageEnded } =
    useContext(PackageDetailsContext)

  const [eventPackage, setEventPackage] = useState()
  const [data, setData] = useState(null)
  const [background, setBackground] = useState(null)

  const [selectedSeatIds, setSelectedSeatIds] = useState({})
  const [seatsInPackage, setSeatsInPackage] = useState({})
  const [soldMap, setSoldMap] = useState(new Map())
  const [reservedMap, setReservedMap] = useState(new Map())
  const [holdMap, setHoldMap] = useState(new Map())
  const [packageSalesBySection, setPackageSalesBySection] = useState({})
  const [highestAttendanceBySection, setHighestAttendanceBySection] = useState(
    {}
  )

  const [initialState, setInitialState] = useState()
  const [isSaving, setIsSaving] = useState(false)
  const [isMapDisabled, setIsMapDisabled] = useState(false)
  const isSavingRef = useRef(false)

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }

    showLoading()
    loadPackage()
  }, [orgPermissionsReady, hasPermission])

  useEffect(() => {
    if (!isAssigningToFan) {
      return
    }

    if (selectedSeatIds && Object.keys(selectedSeatIds).length > 0) {
      openSheet(CurrentSelected, {
        selectedSeatIds: selectedSeatIds,
        handleRemove: clearSelectedSeats,
      })
    } else {
      closeSheet()
    }
  }, [selectedSeatIds])

  useEffect(() => {
    // Assignment stays available after the sale window and after games start.
    if (isAssigningToFan) setIsMapDisabled(false)
    else setIsMapDisabled(hasPackageEnded)
  }, [isAssigningToFan, hasPackageEnded])

  useLayoutEffect(() => {
    if (!orgPermissionsReady || !hasPermission) return

    // if is assign to fan, hide navbar, banner, and sidebar
    if (isAssigningToFan) {
      // hide navbar
      const nav = document.getElementById('navbar')
      nav.style.display = 'none'
      // hide event banner
      const banner = document.getElementById('banner')
      banner.style.display = 'none'
      // hide sidebar
      const sidebarNav = document.getElementById('sidebarMenu')
      sidebarNav.style.display = 'none'

      // modify container
      const container = document.getElementById('main-container')
      fullHeightContainer(container)

      // modify container
      const content = document.getElementById('main-content')
      content.classList.add('pb-0')
      content.classList.add('full-height-wrapper')
      content.classList.remove('spacer-md', 'spacer-md--with-banner')

      return () => {
        nav.style.display = ''
        banner.style.display = ''
        sidebarNav.style.display = ''
        removeFullHeightContainer(container)
        content.classList.remove('pb-0')
        content.classList.remove('full-height-wrapper')
        content.classList.add('spacer-md', 'spacer-md--with-banner')
      }
    }
  }, [isAssigningToFan, hasPermission, orgPermissionsReady])

  const packageTicketsIntoLookup = (packageTickets) => {
    if (!packageTickets) return { seats: {}, gaSeats: {} }

    const result = {
      seats: {},
      gaSeats: {},
    }

    packageTickets.forEach((ticket) => {
      const seatId = normalizeSeatId(ticket?.seatId ?? ticket?.seat_id)
      if (!seatId) return

      const sectionId = normalizeSeatId(ticket?.sectionId ?? ticket?.section_id)
      const isGA = Boolean(ticket?.GA ?? ticket?.ga)

      if (isGA && sectionId) {
        if (!result.gaSeats[sectionId]) {
          result.gaSeats[sectionId] = []
        }
        result.gaSeats[sectionId].push(seatId)
      } else {
        result.seats[seatId] = seatId
      }
    })

    return result
  }

  const sectionSoldCountsToState = (sectionSoldCounts) => {
    const packageSalesBySectionMap = {}
    const highestAttendanceBySectionMap = {}

    sectionSoldCounts.forEach((record) => {
      const { sectionId, eventUUID, packageUUID, soldCount } = record

      if (eventUUID) {
        if (!highestAttendanceBySectionMap[sectionId]) {
          highestAttendanceBySectionMap[sectionId] = 0
        }

        if (soldCount > highestAttendanceBySectionMap[sectionId]) {
          highestAttendanceBySectionMap[sectionId] = soldCount
        }
      }

      if (packageUUID) {
        if (!packageSalesBySectionMap[sectionId]) {
          packageSalesBySectionMap[sectionId] = 0
        }

        packageSalesBySectionMap[sectionId] =
          packageSalesBySectionMap[sectionId] + soldCount
      }
    })

    setPackageSalesBySection(packageSalesBySectionMap)
    setHighestAttendanceBySection(highestAttendanceBySectionMap)
  }

  const loadPackage = async () => {
    try {
      const res = await getPackageMap(uuid, { includeSeatIdentity: false })
      const {
        eventPackage,
        sectionSoldCounts,
        soldSeatIds,
        reservedSeatIds,
        heldSeatIds,
        heldSeatInfoById,
        seatIdentityById,
      } = res.data
      safeSetState(() => {
        setEventPackage(eventPackage)
        setData(eventPackage.seatmap?.mapping)
        setBackground(eventPackage.seatmap?.background)

        sectionSoldCountsToState(sectionSoldCounts)
        const soldSeatsForMap = Array.from(soldSeatIds || [])
          .map((id) => normalizeSeatId(id))
          .filter(Boolean)
          .map((id) => [id, seatIdentityById?.[id] || true])
        setSoldMap(new Map(soldSeatsForMap))
        const reservedSeatsForMap = Array.from(reservedSeatIds || [])
          .map((id) => normalizeSeatId(id))
          .filter(Boolean)
          .map((id) => [id, seatIdentityById?.[id] || true])
        setReservedMap(new Map(reservedSeatsForMap))
        const heldSeatsForMap = (heldSeatIds || [])
          .map((id) => normalizeSeatId(id))
          .filter(Boolean)
          .map((id) => [id, heldSeatInfoById?.[id] || true])
        setHoldMap(new Map(heldSeatsForMap))

        if (!isAssigningToFan) {
          const selectedSeats = packageTicketsIntoLookup(
            eventPackage.package_tickets
          )
          setSelectedSeatIds(selectedSeats)
          setInitialState(JSON.parse(JSON.stringify(selectedSeats)))
        } else {
          const seatsInPkg = packageTicketsIntoLookup(
            eventPackage.package_tickets
          )
          setSeatsInPackage(seatsInPkg)
        }
      })

      loadSeatIdentities()
    } catch (err) {
      console.error(err)
    } finally {
      hideLoading()
    }
  }

  const mergeSeatIdentityIntoMap = (prevMap, seatIdentityById) => {
    if (!prevMap || prevMap.size === 0) return prevMap

    const entries = Array.from(prevMap.entries()).map(([seatId, value]) => [
      seatId,
      seatIdentityById?.[seatId] || value,
    ])

    return new Map(entries)
  }

  const loadSeatIdentities = async () => {
    try {
      const res = await getPackageSeatIdentities(uuid)
      const seatIdentityById = res?.data?.seatIdentityById || {}

      if (Object.keys(seatIdentityById).length === 0) return

      safeSetState(() => {
        setSoldMap((prevMap) =>
          mergeSeatIdentityIntoMap(prevMap, seatIdentityById)
        )
        setReservedMap((prevMap) =>
          mergeSeatIdentityIntoMap(prevMap, seatIdentityById)
        )
      })
    } catch (err) {
      console.error('Failed to load seat identities for tooltips', err)
    }
  }

  // The API reconciles the package against whatever selection it is given, so
  // sold and reserved tickets are always sent back with it. Sections and seat
  // maps that are absent from the selection stay absent, because absence means
  // "don't touch this" rather than "empty this".
  const withCommittedInventory = (selection) => {
    const payload = {}

    if (selection?.seats) {
      payload.seats = { ...selection.seats }
    }

    if (selection?.gaSeats) {
      payload.gaSeats = Object.fromEntries(
        Object.entries(selection.gaSeats).map(([sectionId, seatIds]) => [
          sectionId,
          [...(seatIds || [])],
        ])
      )
    }

    ;(eventPackage?.package_tickets || []).forEach((ticket) => {
      if (ticket?.on_sale_status === 'available') return

      const seatId = normalizeSeatId(ticket?.seatId ?? ticket?.seat_id)
      if (!seatId) return

      const isGA = Boolean(ticket?.GA ?? ticket?.ga)
      const sectionId = normalizeSeatId(ticket?.sectionId ?? ticket?.section_id)

      if (isGA && sectionId) {
        const sectionSeatIds = payload.gaSeats?.[sectionId]
        if (!sectionSeatIds || sectionSeatIds.includes(seatId)) return
        sectionSeatIds.push(seatId)
        return
      }

      if (payload.seats && !payload.seats[seatId]) {
        payload.seats[seatId] = seatId
      }
    })

    return payload
  }

  const confirmInventoryRemoval = ({ removalCount, remainingCount }) => {
    openModal(
      Confirm,
      {
        title: 'Remove most of this package’s inventory?',
        description: `Saving removes ${removalCount} unsold ${
          removalCount === 1 ? 'ticket' : 'tickets'
        } and leaves ${remainingCount} in this package. Sold and reserved tickets are kept. This can’t be undone.`,
        confirmLabel: 'Remove tickets',
        onConfirm: () => {
          closeModal()
          submitPackageInventory({ confirmDestructive: true })
        },
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' }
    )
  }

  const submitPackageInventory = ({ confirmDestructive = false } = {}) => {
    if (isSavingRef.current) return

    isSavingRef.current = true
    setIsSaving(true)

    const dataPack = {
      id: eventPackage.id,
      seatsInPackage: withCommittedInventory(selectedSeatIds),
      mapping: data,
      price: eventPackage.price,
    }

    if (confirmDestructive) {
      dataPack.confirmDestructive = true
    }

    updateSeatsInPackage(dataPack)
      .then((res) => {
        isSavingRef.current = false
        setIsSaving(false)
        if (!isSaving) {
          navigate(`/package/${uuid}/assign-packages`)
        }
        updateCanPublish(name, Object.values(selectedSeatIds))
      })
      .catch((err) => {
        isSavingRef.current = false
        setIsSaving(false)

        const details = err?.response?.data?.error?.details
        if (
          !confirmDestructive &&
          details?.code === 'INVENTORY_REMOVAL_REQUIRES_CONFIRMATION'
        ) {
          confirmInventoryRemoval(details)
          return
        }

        console.error(err)
      })
  }

  const savePackageInventory = () => {
    if (isAssigningToFan || isSavingRef.current) return

    submitPackageInventory()
  }

  const clearSelectedSeats = () => {
    setSelectedSeatIds({})
  }

  if (!orgPermissionsReady) {
    return (
      <PageLoadingContainer
        style={`${isAssigningToFan ? 'without-sidebar' : ''}`}
      />
    )
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return (
      <PageLoadingContainer
        style={`${isAssigningToFan ? 'without-sidebar' : ''}`}
      />
    )
  }

  return (
    <div
      className="package-seatmap-shell"
      aria-busy={isSaving}
      data-saving={isSaving ? 'true' : 'false'}
    >
      <ConfigureMap
        eventPackage={eventPackage}
        isMapDisabled={isMapDisabled}
        isPackagePublished={isPackagePublished}
        hasPackageEnded={hasPackageEnded}
        selectedSeatIds={selectedSeatIds}
        savePackageInventory={savePackageInventory}
        isSaving={isSaving}
        data={data}
        background={background}
        seatsInPackage={seatsInPackage}
        setSelectedSeatIds={setSelectedSeatIds}
        isAssigningToFan={isAssigningToFan}
        soldMap={soldMap}
        reservedMap={reservedMap}
        holdMap={holdMap}
        initialState={initialState}
        packageSalesBySection={packageSalesBySection}
        highestAttendanceBySection={highestAttendanceBySection}
      />
      <SavingOverlay
        active={isSaving}
        title="Saving Seat Map..."
        message="Please keep this page open until saving finishes."
      />
    </div>
  )
}
